import { ID, LinkOptions, Task, UpdateTask } from '@griply/types';
import { resolveGoalTreeLink } from '@griply/utils';
import { useUpdateTaskLinks } from 'features/tasks';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GoalTreeContextMenu } from 'shared/components/connected/goal-context-menu';
import { PopupMenu } from 'shared/components/ui/popup-menu';

import { getSharedLink } from './get-shared-link';

export type LinkedItemSubMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'onOutside'
> & {
  tasks: Task[];
  onClose: () => void;
  onUpdate?: (task: Task, updates: UpdateTask) => void;
};

const buildLinkUpdate = (link: { id: ID; type: LinkOptions } | null) => ({
  goalId: link?.type === LinkOptions.Goal ? link.id : null,
  lifeAreaId: link?.type === LinkOptions.LifeArea ? link.id : null,
});

export const LinkedItemSubMenu: React.FC<LinkedItemSubMenuProps> = ({
  tasks,
  onClose,
  onUpdate,
  ...rest
}) => {
  const { t } = useTranslation();
  const updateTaskLinks = useUpdateTaskLinks();

  const sharedLink = useMemo(() => getSharedLink(tasks), [tasks]);

  const currentSelection = useMemo(() => {
    if (!sharedLink) {
      return undefined;
    }
    return sharedLink.type === LinkOptions.Goal
      ? { goalId: sharedLink.id }
      : { lifeAreaId: sharedLink.id };
  }, [sharedLink]);

  const onChange = (link: { id: ID; type: LinkOptions } | null) => {
    const linkUpdate = buildLinkUpdate(link);

    if (onUpdate) {
      tasks.forEach((task) => onUpdate(task, { id: task.id, ...linkUpdate }));
      onClose();
      return;
    }

    // The seam widens the scalar write to the link arrays and applies the goal-count deltas.
    updateTaskLinks(
      tasks.map((task) => ({
        task,
        update: { id: task.id, ...linkUpdate },
      })),
    );

    onClose();
  };

  const onSelect = (ancestorIds: ID[], lifeAreaId: ID | null) =>
    onChange(resolveGoalTreeLink(ancestorIds, lifeAreaId));

  const onClear = () => onChange(null);

  return (
    <GoalTreeContextMenu
      {...rest}
      currentSelection={currentSelection}
      onSelect={onSelect}
      onClear={sharedLink ? onClear : undefined}
      clearLabel={t(
        sharedLink?.type === LinkOptions.LifeArea
          ? 'task.linked-item.life-area.options.remove.label'
          : 'task.linked-item.goal.options.remove.label',
      )}
      onClose={onClose}
    />
  );
};
